import { useState, useEffect } from 'react';
import { getKeywords, addKeyword, deleteKeyword, getFlagged, getReview, deleteReviewItem, deleteFlaggedItem, getViolations, deleteViolation } from '../api';

const CATEGORIES = [
  { value: 'narcotics',         label: 'Narcotics',          color: '#FF3B30', bg: '#FFF0EF' },
  { value: 'adult',             label: 'Adult Content',      color: '#FF9500', bg: '#FFF8EC' },
  { value: 'regulated',         label: 'Regulated Goods',    color: '#007AFF', bg: '#EBF4FF' },
  { value: 'financial_schemes', label: 'Financial Schemes',  color: '#5856D6', bg: '#F0EFFF' },
  { value: 'gambling',          label: 'Gambling',           color: '#00C48C', bg: '#E6FAF5' },
  { value: 'fraud',             label: 'Fraud',              color: '#FF2D55', bg: '#FFF0F3' },
  { value: 'other',             label: 'Other',              color: '#6E6E73', bg: '#F5F5F7' },
];

function catMeta(cat) {
  return CATEGORIES.find(c => c.value === cat) || CATEGORIES[CATEGORIES.length - 1];
}

export default function KeywordsPage() {
  const [keywords, setKeywords]     = useState([]);
  const [flagged, setFlagged]       = useState([]);
  const [review, setReview]         = useState([]);
  const [violations, setViolations] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [newWord, setNewWord]       = useState('');
  const [newCat, setNewCat]         = useState('narcotics');
  const [newScore, setNewScore]     = useState(90);
  const CAT_DEFAULT_SCORES = { narcotics:90, fraud:90, adult:90, regulated:75, financial_schemes:65, gambling:55, other:40 };

  const [adding, setAdding]         = useState(false);
  const [filterCat, setFilterCat]   = useState('');
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [activeTab, setActiveTab]   = useState('keywords');

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [kw, fl, rv, vl] = await Promise.all([getKeywords(), getFlagged({ limit: 50 }), getReview({ limit: 50 }), getViolations({ limit: 50 })]);
      setKeywords(kw);
      setFlagged(fl.items || []);
      setReview(rv.items || []);
      setViolations(vl.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function handleDeleteViolation(id) {
    if (!window.confirm('Remove this violation record?')) return;
    try {
      await deleteViolation(id);
      setViolations(prev => prev.filter(v => v.id !== id));
    } catch (e) { console.error(e); }
  }

  async function handleDeleteReview(id) {
    if (!window.confirm('Remove this review record?')) return;
    try {
      await deleteReviewItem(id);
      setReview(prev => prev.filter(r => r.id !== id));
    } catch (e) { console.error(e); }
  }

  async function handleDeleteFlagged(id) {
    if (!window.confirm('Remove this flagged record?')) return;
    try {
      await deleteFlaggedItem(id);
      setFlagged(prev => prev.filter(r => r.id !== id));
    } catch (e) { console.error(e); }
  }

  async function handleAdd() {
    if (!newWord.trim()) return;
    setAdding(true); setError(''); setSuccess('');
    try {
      await addKeyword(newWord.trim(), newCat, newScore);
      setSuccess(`"${newWord.trim()}" added`);
      setNewWord('');
      const kw = await getKeywords();
      setKeywords(kw);
    } catch (e) { setError(e.message || 'Failed to add keyword'); }
    finally { setAdding(false); }
  }

  async function handleDelete(id, word) {
    if (!window.confirm(`Remove "${word}" from blocked list?`)) return;
    try {
      await deleteKeyword(id);
      setKeywords(prev => prev.filter(k => k.id !== id));
    } catch (e) { setError('Failed to delete'); }
  }

  const grouped = CATEGORIES.reduce((acc, cat) => {
    acc[cat.value] = keywords.filter(k => k.category === cat.value);
    return acc;
  }, {});

  const displayed = filterCat
    ? keywords.filter(k => k.category === filterCat)
    : keywords;

  if (loading) return <div style={s.loading}>Loading...</div>;

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Content Moderation</h1>
          <p style={s.subtitle}>{keywords.length} blocked keywords · {flagged.length} flagged · {review.length} review · {violations.length} velocity</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabRow}>
        {['keywords','review','flagged','velocity'].map(t => (
          <button key={t} style={{...s.tab, ...(activeTab===t ? s.tabActive : {})}} onClick={() => setActiveTab(t)}>
            {t === 'keywords' ? `Keywords (${keywords.length})` : t === 'review' ? `Needs Review (${review.length})` : t === 'velocity' ? `Velocity (${violations.length})` : `Blocked (${flagged.length})`}
          </button>
        ))}
      </div>

      {activeTab === 'keywords' && (
        <>
          {/* Add keyword */}
          <div style={s.addCard}>
            <div style={s.addTitle}>Add keyword</div>
            <div style={s.addRow}>
              <input
                style={s.addInput}
                type="text"
                placeholder="e.g. cocaine, escort, pyramid scheme..."
                value={newWord}
                onChange={e => setNewWord(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
              />
              <select style={s.addSelect} value={newCat} onChange={e => { setNewCat(e.target.value); setNewScore(CAT_DEFAULT_SCORES[e.target.value] || 75); }}>
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <button style={s.addBtn} onClick={handleAdd} disabled={adding || !newWord.trim()}>
                {adding ? 'Adding...' : '+ Add'}
              </button>
            </div>
            {error   && <div style={s.error}>{error}</div>}
            {success && <div style={s.successMsg}>{success}</div>}
          </div>

          {/* Category filter pills */}
          <div style={s.filterRow}>
            <button style={{...s.filterPill, ...(filterCat==='' ? s.filterPillActive : {})}} onClick={() => setFilterCat('')}>
              All ({keywords.length})
            </button>
            {CATEGORIES.map(c => (
              <button key={c.value}
                style={{...s.filterPill, ...(filterCat===c.value ? {...s.filterPillActive, background: c.color, borderColor: c.color} : {})}}
                onClick={() => setFilterCat(c.value)}>
                {c.label} ({grouped[c.value]?.length || 0})
              </button>
            ))}
          </div>

          {/* Keyword list */}
          <div style={s.card}>
            {displayed.length === 0 ? (
              <div style={s.empty}>No keywords in this category</div>
            ) : (
              <div style={s.kwGrid}>
                {displayed.map(kw => {
                  const meta = catMeta(kw.category);
                  return (
                    <div key={kw.id} style={s.kwChip}>
                      <span style={{...s.kwCatDot, background: meta.color}}/>
                      <span style={s.kwText}>{kw.keyword}</span>
                      <span style={{...s.kwCatLabel, color: meta.color, background: meta.bg}}>{meta.label}</span>
                      <button style={s.kwDelete} onClick={() => handleDelete(kw.id, kw.keyword)} title="Remove">✕</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'review' && (
        <div style={s.card}>
          {review.length === 0 ? (
            <div style={s.empty}>No items pending review</div>
          ) : (
            review.map((f, i) => {
              const meta = catMeta(f.triggered_category);
              return (
                <div key={f.id} style={{...s.flagRow, borderBottom: i < review.length-1 ? '1px solid #F5F5F7' : 'none'}}>
                  <div style={s.flagTop}>
                    <div>
                      <div style={{...s.flagUser, cursor:'pointer', color:'#007AFF'}} onClick={() => window.location.href='/users?q='+encodeURIComponent(f.user_name||f.user_email||'')}>{f.user_name || 'Unknown'}</div>
                      {f.user_email && <div style={{...s.flagEmail, cursor:'pointer', color:'#007AFF'}} onClick={() => window.location.href='/users?q='+encodeURIComponent(f.user_name||f.user_email||'')}>{f.user_email}</div>}
                    </div>
                    <div style={s.flagRight}>
                      <span style={{fontSize:11, fontWeight:700, color:'#FF9500', background:'#FFF8EC', padding:'2px 8px', borderRadius:6}}>Score: {f.score}</span>
                      <span style={{...s.kwCatLabel, color: meta.color, background: meta.bg}}>{meta.label}</span>
                      <span style={s.flagTime}>{new Date(f.created_at * 1000).toLocaleDateString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
                    </div>
                  </div>
                  <div style={s.flagDetail}>
                    {f.description && <span style={s.flagDesc}>Description: <em>"{f.description}"</em></span>}
                    {f.merchant_name && <span style={s.flagDesc}> · Merchant: <em>"{f.merchant_name}"</em></span>}
                  </div>
                  <div style={s.flagKeyword}>
                    Triggered by: <strong style={{color: meta.color}}>"{f.triggered_keyword}"</strong>
                    <span style={{color:'#AEAEB2', marginLeft:8}}>{f.match_type} match</span>
                    <button onClick={() => handleDeleteReview(f.id)} style={s.deleteBtn} title="Remove record">✕</button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {activeTab === 'velocity' && (
        <div style={s.card}>
          {violations.length === 0 ? (
            <div style={s.empty}>No velocity violations yet</div>
          ) : (
            violations.map((v, i) => {
              const typeLabels = {
                p2p_weekly_limit:     'P2P Weekly Limit',
                cashout_single_limit: 'Cashout Single Limit',
                cashout_weekly_limit: 'Cashout Weekly Limit',
                guest_daily_limit:    'Guest Daily Limit',
                guest_weekly_limit:   'Guest Weekly Limit',
              };
              const isGuest = v.user_id === 'guest';
              return (
                <div key={v.id} style={{...s.flagRow, borderBottom: i < violations.length-1 ? '1px solid #F5F5F7' : 'none'}}>
                  <div style={s.flagTop}>
                    <div>
                      {isGuest
                        ? <div style={s.flagUser}>Guest — IP: {v.user_name}</div>
                        : <div style={{...s.flagUser, cursor:'pointer', color:'#007AFF'}} onClick={() => window.location.href='/users?q='+encodeURIComponent(v.user_name||v.user_email||'')}>{v.user_name || 'Unknown'}</div>
                      }
                      {v.user_email && !isGuest && <div style={{...s.flagEmail, cursor:'pointer', color:'#007AFF'}} onClick={() => window.location.href='/users?q='+encodeURIComponent(v.user_name||v.user_email||'')}>{v.user_email}</div>}
                    </div>
                    <div style={s.flagRight}>
                      <span style={{fontSize:11, fontWeight:600, color:'#FF3B30', background:'#FFF0EF', padding:'2px 8px', borderRadius:6}}>
                        {typeLabels[v.violation_type] || v.violation_type}
                      </span>
                      <span style={s.flagTime}>{new Date(v.created_at * 1000).toLocaleDateString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
                    </div>
                  </div>
                  <div style={s.flagDetail}>
                    <span style={s.flagDesc}>Attempted: <strong>${(v.attempted_cents/100).toFixed(2)}</strong></span>
                    <span style={s.flagDesc}> · Limit: <strong>${(v.limit_cents/100).toFixed(2)}</strong></span>
                    {v.weekly_total_cents > 0 && <span style={s.flagDesc}> · Week total so far: <strong>${(v.weekly_total_cents/100).toFixed(2)}</strong></span>}
                  </div>
                  <div style={s.flagKeyword}>
                    Over limit by: <strong style={{color:'#FF3B30'}}>${((v.attempted_cents - v.limit_cents)/100).toFixed(2)}</strong>
                    <button onClick={() => handleDeleteViolation(v.id)} style={s.deleteBtn} title="Remove record">✕</button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {activeTab === 'flagged' && (
        <div style={s.card}>
          {flagged.length === 0 ? (
            <div style={s.empty}>No flagged attempts yet</div>
          ) : (
            flagged.map((f, i) => {
              const meta = catMeta(f.triggered_category);
              return (
                <div key={f.id} style={{...s.flagRow, borderBottom: i < flagged.length-1 ? '1px solid #F5F5F7' : 'none'}}>
                  <div style={s.flagTop}>
                    <div>
                      <div style={{...s.flagUser, cursor:'pointer', color:'#007AFF'}} onClick={() => window.location.href='/users?q='+encodeURIComponent(f.user_name||f.user_email||'')}>{f.user_name || 'Unknown'}</div>
                      {f.user_email && <div style={{...s.flagEmail, cursor:'pointer', color:'#007AFF'}} onClick={() => window.location.href='/users?q='+encodeURIComponent(f.user_name||f.user_email||'')}>{f.user_email}</div>}
                    </div>
                    <div style={s.flagRight}>
                      <span style={{...s.kwCatLabel, color: meta.color, background: meta.bg}}>{meta.label}</span>
                      <span style={s.flagTime}>
                        {new Date(f.created_at * 1000).toLocaleDateString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}
                      </span>
                    </div>
                  </div>
                  <div style={s.flagDetail}>
                    {f.description && <span style={s.flagDesc}>Description: <em>"{f.description}"</em></span>}
                    {f.merchant_name && <span style={s.flagDesc}> · Merchant: <em>"{f.merchant_name}"</em></span>}
                  </div>
                  <div style={s.flagKeyword}>
                    Triggered by: <strong style={{color: meta.color}}>"{f.triggered_keyword}"</strong>
                    <button onClick={() => handleDeleteFlagged(f.id)} style={s.deleteBtn} title="Remove record">✕</button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const s = {
  loading:        { padding:40, color:'#6E6E73' },
  header:         { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 },
  title:          { fontSize:28, fontWeight:700, color:'#1D1D1F' },
  subtitle:       { fontSize:14, color:'#6E6E73', marginTop:4 },
  tabRow:         { display:'flex', background:'#F5F5F7', borderRadius:12, padding:4, gap:4, marginBottom:24, width:'fit-content' },
  tab:            { padding:'8px 20px', fontSize:14, fontWeight:500, border:'none', borderRadius:8, background:'transparent', color:'#6E6E73', cursor:'pointer' },
  tabActive:      { background:'#fff', color:'#1D1D1F', boxShadow:'0 1px 4px rgba(0,0,0,0.1)' },
  addCard:        { background:'#fff', borderRadius:16, padding:20, marginBottom:20, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  addTitle:       { fontSize:14, fontWeight:600, color:'#1D1D1F', marginBottom:12 },
  addRow:         { display:'flex', gap:10, alignItems:'center' },
  addInput:       { flex:1, padding:'11px 14px', border:'1.5px solid #E5E5EA', borderRadius:10, fontSize:14, color:'#1D1D1F', outline:'none', background:'#FAFAFA' },
  addSelect:      { padding:'11px 12px', border:'1.5px solid #E5E5EA', borderRadius:10, fontSize:13, color:'#1D1D1F', outline:'none', background:'#FAFAFA', cursor:'pointer' },
  addBtn:         { padding:'11px 20px', background:'#007AFF', color:'#fff', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' },
  error:          { marginTop:10, fontSize:13, color:'#FF3B30' },
  successMsg:     { marginTop:10, fontSize:13, color:'#00C48C' },
  filterRow:      { display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 },
  filterPill:     { padding:'6px 14px', border:'1.5px solid #E5E5EA', borderRadius:20, fontSize:12, fontWeight:500, color:'#6E6E73', background:'#fff', cursor:'pointer' },
  filterPillActive:{ background:'#007AFF', borderColor:'#007AFF', color:'#fff' },
  card:           { background:'#fff', borderRadius:16, padding:20, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  empty:          { padding:'20px 0', textAlign:'center', color:'#AEAEB2', fontSize:14 },
  kwGrid:         { display:'flex', flexWrap:'wrap', gap:8 },
  kwChip:         { display:'flex', alignItems:'center', gap:6, padding:'6px 10px 6px 8px', background:'#F5F5F7', borderRadius:10, border:'1px solid #E5E5EA' },
  kwCatDot:       { width:8, height:8, borderRadius:'50%', flexShrink:0 },
  kwText:         { fontSize:13, fontWeight:500, color:'#1D1D1F' },
  kwCatLabel:     { fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:6 },
  kwDelete:       { background:'none', border:'none', color:'#AEAEB2', fontSize:12, cursor:'pointer', padding:0, lineHeight:1, marginLeft:2 },
  flagRow:        { padding:'14px 0' },
  flagTop:        { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 },
  flagUser:       { fontSize:14, fontWeight:600, color:'#1D1D1F' },
  flagEmail:      { fontSize:12, color:'#6E6E73' },
  flagRight:      { display:'flex', alignItems:'center', gap:8 },
  flagTime:       { fontSize:11, color:'#AEAEB2' },
  flagDetail:     { fontSize:13, color:'#6E6E73', marginBottom:4 },
  flagDesc:       { fontSize:13, color:'#6E6E73' },
  flagKeyword:    { fontSize:13, color:'#3D3D3D' },
  deleteBtn:      { background:'none', border:'none', color:'#FF3B30', fontSize:13, cursor:'pointer', marginLeft:12, padding:0, fontWeight:600 },
};
